import type { ThumbnailImage } from "@videojs/core";
import { Thumbnail } from "@videojs/react";
import { useEffect, useState } from "react";

import { palette, radius, type } from "@/design/tokens";
import { parseStoryboardVtt } from "@/playback/storyboardThumbnails";

import type { RetentionStoryboardPreviewProps } from "./RetentionStoryboardPreview.types";

// Match the watch player's thumbnail constraints. Video.js owns the final
// dimensions so its container always matches the selected VTT sprite crop.
const PREVIEW_MAX_WIDTH = 176;
const PREVIEW_MAX_HEIGHT = 128;
const COMPACT_PREVIEW_MAX_WIDTH = 132;
const COMPACT_PREVIEW_MAX_HEIGHT = 96;
const storyboardCache = new Map<string, Promise<ThumbnailImage[] | undefined>>();

function loadStoryboard(storyboardVttUrl: string): Promise<ThumbnailImage[] | undefined> {
  const cached = storyboardCache.get(storyboardVttUrl);
  if (cached) return cached;
  const pending = fetch(storyboardVttUrl)
    .then((response) => {
      if (!response.ok) throw new Error(`Storyboard VTT failed: ${response.status}`);
      return response.text();
    })
    .then((text) => {
      const next = parseStoryboardVtt(text, storyboardVttUrl);
      return next.length > 0 ? next : undefined;
    })
    .catch(() => {
      storyboardCache.delete(storyboardVttUrl);
      return undefined;
    });
  storyboardCache.set(storyboardVttUrl, pending);
  return pending;
}

/** Reuses the real Video.js sprite renderer used by the watch-player timeline. */
export function RetentionStoryboardPreview({
  compact = false,
  storyboardVttUrl,
  time,
  timeLabel,
  left,
  top = 8
}: RetentionStoryboardPreviewProps) {
  const [sprites, setSprites] = useState<ThumbnailImage[] | undefined>();

  useEffect(() => {
    if (!storyboardVttUrl) {
      setSprites(undefined);
      return;
    }
    let active = true;
    setSprites(undefined);
    void loadStoryboard(storyboardVttUrl).then((next) => {
      if (active) setSprites(next);
    });
    return () => {
      active = false;
    };
  }, [storyboardVttUrl]);

  if (!sprites) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        left,
        pointerEvents: "none",
        position: "absolute",
        top,
        transform: "translateX(-50%)",
        zIndex: 2
      }}
    >
      <div
        style={{
          background: palette.playerBg,
          border: `1px solid ${palette.hairlineStrong}`,
          borderRadius: radius.sm,
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.38)",
          display: "flex",
          overflow: "hidden",
        }}
      >
        <Thumbnail
          thumbnails={sprites}
          time={time}
          style={{
            display: "block",
            maxHeight: compact ? COMPACT_PREVIEW_MAX_HEIGHT : PREVIEW_MAX_HEIGHT,
            maxWidth: compact ? COMPACT_PREVIEW_MAX_WIDTH : PREVIEW_MAX_WIDTH
          }}
        />
      </div>
      <div
        style={{
          background: palette.surfaceOverlay,
          borderRadius: radius.pill,
          color: palette.text,
          fontFamily: type.smallStrong.fontFamily,
          fontSize: `${type.smallStrong.fontSize}px`,
          lineHeight: `${type.smallStrong.lineHeight}px`,
          marginTop: 4,
          padding: "2px 8px"
        }}
      >
        {timeLabel}
      </div>
    </div>
  );
}
